<div class="flex h-full flex-col p-5" (click)="closeMenu()">

  <!-- Page header -->
  <div class="mb-4 flex items-center justify-between">
    <div>
      <h1 class="text-sm font-semibold text-zinc-900">User Command Center</h1>
      <p class="mt-0.5 text-xs text-zinc-500">{{ totalCount }} users loaded</p>
    </div>
    <div class="flex items-center gap-2">
      <button
        (click)="loadUsers()"
        class="flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-50"
      >
        <lucide-icon name="refresh-cw" [size]="12" [strokeWidth]="1.75"></lucide-icon>
        Refresh
      </button>
      <button
        (click)="openCreatePanel()"
        class="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-700"
      >
        <lucide-icon name="users" [size]="13" [strokeWidth]="2"></lucide-icon>
        Create admin
      </button>
    </div>
  </div>

  <!-- Error banner -->
  <div *ngIf="error" class="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">
    <lucide-icon name="circle-alert" [size]="13" [strokeWidth]="2"></lucide-icon>
    {{ error }}
  </div>

  <!-- Table card -->
  <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white">

    <!-- Filter bar -->
    <div class="flex flex-shrink-0 items-center gap-2 border-b border-zinc-200 px-4 py-2.5">
      <div class="relative w-56">
        <span class="pointer-events-none absolute inset-y-0 left-0 flex w-7 items-center justify-center">
          <lucide-icon name="search" [size]="13" [strokeWidth]="1.75" class="text-zinc-400"></lucide-icon>
        </span>
        <input
          type="text"
          [(ngModel)]="searchQuery"
          placeholder="Name, email or org..."
          class="h-7 w-full rounded-md border border-zinc-200 bg-zinc-50 pl-7 pr-3 text-xs text-zinc-900 placeholder:text-zinc-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-100"
        />
      </div>

      <select
        [(ngModel)]="statusFilter"
        class="h-7 rounded-md border border-zinc-200 bg-zinc-50 px-2 pr-6 text-xs text-zinc-700 focus:border-violet-400 focus:outline-none"
      >
        <option value="">All statuses</option>
        <option value="active">Active</option>
        <option value="pending">Pending</option>
        <option value="suspended">Suspended</option>
        <option value="inactive">Inactive</option>
      </select>

      <select
        [(ngModel)]="roleFilter"
        class="h-7 rounded-md border border-zinc-200 bg-zinc-50 px-2 pr-6 text-xs text-zinc-700 focus:border-violet-400 focus:outline-none"
      >
        <option value="">All roles</option>
        <option value="admin">Admin</option>
        <option value="member">Member</option>
        <option value="viewer">Viewer</option>
      </select>

      <button
        (click)="toggleShowDeleted()"
        class="ml-auto flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors"
        [class.border-red-200]="showDeleted"
        [class.bg-red-50]="showDeleted"
        [class.text-red-700]="showDeleted"
        [class.border-zinc-200]="!showDeleted"
        [class.bg-white]="!showDeleted"
        [class.text-zinc-500]="!showDeleted"
      >
        <lucide-icon name="trash-2" [size]="11" [strokeWidth]="1.75"></lucide-icon>
        {{ showDeleted ? 'Hide deleted' : 'Show deleted' }}
      </button>

      <span class="text-xs text-zinc-400">{{ filteredUsers.length }} of {{ totalCount }}</span>
    </div>

    <!-- Table -->
    <div class="flex-1 overflow-auto">
      <table class="w-full">
        <thead class="sticky top-0 z-10 bg-zinc-50">
          <tr class="border-b border-zinc-200">
            <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">User</th>
            <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Organization</th>
            <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Role</th>
            <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Status</th>
            <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Joined</th>
            <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Last Active</th>
            <th class="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-zinc-100">

          <!-- Skeleton rows while loading -->
          <ng-container *ngIf="loading && users.length === 0">
            <tr *ngFor="let _ of [0,1,2,3,4,5,6,7,8]" class="animate-pulse">
              <td class="px-4 py-3">
                <div class="flex items-center gap-2.5">
                  <div class="h-7 w-7 rounded-full bg-zinc-100"></div>
                  <div>
                    <div class="h-3 w-28 rounded bg-zinc-100"></div>
                    <div class="mt-1.5 h-2.5 w-36 rounded bg-zinc-100"></div>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3"><div class="h-3 w-24 rounded bg-zinc-100"></div></td>
              <td class="px-4 py-3"><div class="h-5 w-14 rounded bg-zinc-100"></div></td>
              <td class="px-4 py-3"><div class="h-5 w-10 rounded bg-zinc-100"></div></td>
              <td class="px-4 py-3"><div class="h-5 w-16 rounded bg-zinc-100"></div></td>
              <td class="px-4 py-3"><div class="h-3 w-20 rounded bg-zinc-100"></div></td>
              <td class="px-4 py-3"><div class="h-3 w-20 rounded bg-zinc-100"></div></td>
              <td class="px-4 py-3 text-right"><div class="ml-auto h-6 w-16 rounded bg-zinc-100"></div></td>
            </tr>
          </ng-container>

          <!-- Data rows -->
          <tr
            *ngFor="let user of filteredUsers"
            class="group transition-colors hover:bg-zinc-50"
            [class.opacity-60]="user.deletedAt"
          >
            <!-- User identity -->
            <td class="px-4 py-2.5">
              <div class="flex items-center gap-2.5">
                <div
                  class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                  [class.bg-violet-100]="!user.deletedAt"
                  [class.text-violet-700]="!user.deletedAt"
                  [class.bg-zinc-100]="!!user.deletedAt"
                  [class.text-zinc-400]="!!user.deletedAt"
                >{{ user.initials }}</div>
                <div>
                  <p class="text-xs font-medium" [class.text-zinc-900]="!user.deletedAt" [class.text-zinc-400]="!!user.deletedAt" [class.line-through]="!!user.deletedAt">{{ user.name }}</p>
                  <p class="text-[11px] text-zinc-400">{{ user.email }}</p>
                </div>
              </div>
            </td>

            <!-- Organization -->
            <td class="px-4 py-2.5">
              <p class="text-xs text-zinc-700">{{ user.orgName }}</p>
              <p *ngIf="user.orgRole !== '—'" class="mt-0.5 text-[10px] capitalize text-zinc-400">{{ user.orgRole }}</p>
            </td>

            <!-- Platform role -->
            <td class="px-4 py-2.5">
              <span class="rounded px-1.5 py-0.5 text-[11px] font-medium capitalize" [ngClass]="roleBadge(user.role)">{{ user.role }}</span>
            </td>

            <!-- Status -->
            <td class="px-4 py-2.5">
              <span class="rounded px-1.5 py-0.5 text-[11px] font-medium capitalize" [ngClass]="statusBadge(user.status)">{{ user.status }}</span>
            </td>

            <!-- Joined -->
            <td class="px-4 py-2.5 text-[11px] text-zinc-500">{{ user.joined | date:'MMM d, y' }}</td>

            <!-- Last active (updatedAt proxy) -->
            <td class="px-4 py-2.5 text-[11px] text-zinc-500">{{ user.updatedAt | date:'MMM d, y' }}</td>

            <!-- Actions -->
            <td class="px-4 py-2.5 text-right">
              <div class="relative inline-flex items-center gap-1" (click)="$event.stopPropagation()">
                <button
                  (click)="viewUser(user)"
                  class="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                >
                  <lucide-icon name="eye" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                  View
                </button>
                <button
                  (click)="toggleMenu(user.id, $event)"
                  class="flex h-6 w-6 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600"
                >
                  <lucide-icon name="ellipsis" [size]="14" [strokeWidth]="1.75"></lucide-icon>
                </button>

                <!-- Dropdown -->
                <div
                  *ngIf="openMenuId === user.id"
                  class="absolute right-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg"
                >
                  <button (click)="viewUser(user)" class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50">
                    <lucide-icon name="eye" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                    View details
                  </button>

                  <ng-container *ngIf="!user.deletedAt">
                    <button *ngIf="user.status === 'pending_onboarding'" (click)="activateUser(user)" class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50">
                      <lucide-icon name="user-check" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                      Activate account
                    </button>
                    <button (click)="resetPassword(user)" class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50">
                      <lucide-icon name="key" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                      Reset password
                    </button>
                    <button *ngIf="user.role !== 'admin'" (click)="impersonate(user)" class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50">
                      <lucide-icon name="user-check" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                      Impersonate
                    </button>
                    <button
                      *ngIf="user.status !== 'pending_onboarding'"
                      (click)="toggleStatus(user)"
                      class="flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-50"
                      [class.text-amber-600]="user.status !== 'suspended'"
                      [class.text-emerald-600]="user.status === 'suspended'"
                    >
                      <lucide-icon name="user-x" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                      {{ user.status === 'suspended' ? 'Reactivate' : 'Suspend' }}
                    </button>
                    <div class="border-t border-zinc-100"></div>
                    <button (click)="softDelete(user)" class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
                      <lucide-icon name="trash-2" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                      Delete user
                    </button>
                  </ng-container>

                  <ng-container *ngIf="user.deletedAt">
                    <div class="border-t border-zinc-100"></div>
                    <button (click)="restoreUser(user)" class="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50">
                      <lucide-icon name="rotate-ccw" [size]="12" [strokeWidth]="1.75"></lucide-icon>
                      Restore record
                    </button>
                  </ng-container>
                </div>
              </div>
            </td>
          </tr>

          <!-- Empty state -->
          <tr *ngIf="!loading && filteredUsers.length === 0">
            <td colspan="8" class="px-4 py-12 text-center text-xs text-zinc-400">
              No users match the current filters.
            </td>
          </tr>

        </tbody>
      </table>
    </div>

    <!-- Load more -->
    <div *ngIf="nextPageToken" class="flex flex-shrink-0 items-center justify-center border-t border-zinc-200 py-2.5">
      <button
        (click)="loadMore()"
        [disabled]="loading"
        class="flex items-center gap-1.5 text-xs font-medium text-violet-600 hover:text-violet-700 disabled:opacity-50"
      >
        <lucide-icon *ngIf="loading" name="loader" [size]="12" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        {{ loading ? 'Loading...' : 'Load more' }}
      </button>
    </div>

  </div>

</div>

<!-- ── Confirmation modal ───────────────────────────────────────────────── -->
<div
  *ngIf="confirmType"
  class="fixed inset-0 z-50 flex items-center justify-center p-4"
  (click)="cancelConfirm()"
>
  <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]"></div>
  <div
    class="relative w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl"
    (click)="$event.stopPropagation()"
  >
    <div class="px-5 pb-4 pt-5">
      <div
        class="mb-3 flex h-10 w-10 items-center justify-center rounded-full"
        [class.bg-red-100]="confirmType === 'delete'"
        [class.bg-amber-100]="confirmType === 'suspend'"
        [class.bg-emerald-100]="confirmType === 'reactivate' || confirmType === 'activate'"
        [class.bg-blue-100]="confirmType === 'reset-password'"
      >
        <lucide-icon
          [name]="confirmType === 'delete' ? 'trash-2' : confirmType === 'reset-password' ? 'key' : confirmType === 'activate' ? 'user-check' : 'user-x'"
          [size]="18"
          [strokeWidth]="1.75"
          [class.text-red-600]="confirmType === 'delete'"
          [class.text-amber-600]="confirmType === 'suspend'"
          [class.text-emerald-600]="confirmType === 'reactivate' || confirmType === 'activate'"
          [class.text-blue-600]="confirmType === 'reset-password'"
        ></lucide-icon>
      </div>
      <p class="text-sm font-semibold text-zinc-900">
        {{ confirmType === 'delete' ? 'Delete user' :
           confirmType === 'suspend' ? 'Suspend user' :
           confirmType === 'reactivate' ? 'Reactivate user' :
           confirmType === 'activate' ? 'Activate account' :
           'Reset password' }}
      </p>
      <p class="mt-1.5 text-xs leading-relaxed text-zinc-500">{{ confirmMessage }}</p>
    </div>
    <div class="flex items-center justify-end gap-2 border-t border-zinc-100 bg-zinc-50 px-5 py-3">
      <button
        (click)="cancelConfirm()"
        [disabled]="confirming"
        class="rounded-lg border border-zinc-200 px-4 py-2 text-xs font-medium text-zinc-600 hover:bg-zinc-100 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        (click)="executeConfirm()"
        [disabled]="confirming"
        class="flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        [class.bg-red-600]="confirmType === 'delete'"
        [class.hover:bg-red-700]="confirmType === 'delete'"
        [class.bg-amber-600]="confirmType === 'suspend'"
        [class.hover:bg-amber-700]="confirmType === 'suspend'"
        [class.bg-emerald-600]="confirmType === 'reactivate' || confirmType === 'activate'"
        [class.hover:bg-emerald-700]="confirmType === 'reactivate' || confirmType === 'activate'"
        [class.bg-blue-600]="confirmType === 'reset-password'"
        [class.hover:bg-blue-700]="confirmType === 'reset-password'"
      >
        <lucide-icon *ngIf="confirming" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
        {{ confirming ? 'Working...' :
           confirmType === 'delete' ? 'Delete' :
           confirmType === 'suspend' ? 'Suspend' :
           confirmType === 'reactivate' ? 'Reactivate' :
           confirmType === 'activate' ? 'Activate' :
           'Send reset email' }}
      </button>
    </div>
  </div>
</div>

<!-- ── Toast notification ──────────────────────────────────────────────── -->
<div
  *ngIf="toast"
  class="fixed bottom-5 right-5 z-[60] flex items-center gap-2.5 rounded-lg px-4 py-3 text-xs font-medium text-white shadow-lg"
  [class.bg-emerald-600]="toast.type === 'success'"
  [class.bg-red-600]="toast.type === 'error'"
>
  <lucide-icon [name]="toast.type === 'success' ? 'circle-check' : 'circle-alert'" [size]="14" [strokeWidth]="2"></lucide-icon>
  {{ toast.message }}
  <button (click)="dismissToast()" class="ml-1 opacity-70 hover:opacity-100">
    <lucide-icon name="circle-x" [size]="14" [strokeWidth]="2"></lucide-icon>
  </button>
</div>

<!-- ── View user drawer ────────────────────────────────────────────────── -->
<div
  class="fixed inset-0 z-50"
  [class.pointer-events-none]="!selectedUser"
  (click)="closeDrawer()"
>
  <div
    class="absolute inset-0 bg-black/30 backdrop-blur-[1px] transition-opacity duration-200"
    [class.opacity-0]="!selectedUser"
    [class.opacity-100]="!!selectedUser"
  ></div>
  <div
    class="absolute inset-y-0 right-0 flex w-[380px] flex-col bg-white shadow-2xl transition-transform duration-200"
    [class.translate-x-full]="!selectedUser"
    [class.translate-x-0]="!!selectedUser"
    (click)="$event.stopPropagation()"
  >
    <div class="flex flex-shrink-0 items-center justify-between border-b border-zinc-200 px-5 py-4">
      <p class="text-sm font-semibold text-zinc-900">User details</p>
      <button (click)="closeDrawer()" class="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600">
        <lucide-icon name="circle-x" [size]="16" [strokeWidth]="1.75"></lucide-icon>
      </button>
    </div>
    <div *ngIf="selectedUser" class="flex-1 overflow-y-auto px-5 py-5">
      <div class="mb-5 flex items-center gap-3">
        <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-700">{{ selectedUser.initials }}</div>
        <div>
          <p class="text-sm font-semibold text-zinc-900" [class.line-through]="!!selectedUser.deletedAt">{{ selectedUser.name }}</p>
          <p class="text-xs text-zinc-400">{{ selectedUser.email }}</p>
        </div>
      </div>
      <div class="space-y-3 text-xs">
        <div class="flex items-center justify-between">
          <span class="text-zinc-500">Status</span>
          <span class="rounded px-1.5 py-0.5 font-medium capitalize" [ngClass]="statusBadge(selectedUser.status)">{{ selectedUser.status }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-zinc-500">Platform role</span>
          <span class="rounded px-1.5 py-0.5 font-medium capitalize" [ngClass]="roleBadge(selectedUser.role)">{{ selectedUser.role }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-zinc-500">Organization</span>
          <span class="font-medium text-zinc-700">{{ selectedUser.orgName }}</span>
        </div>
        <div *ngIf="selectedUser.orgRole !== '—'" class="flex items-center justify-between">
          <span class="text-zinc-500">Org role</span>
          <span class="capitalize text-zinc-700">{{ selectedUser.orgRole }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-zinc-500">Joined</span>
          <span class="text-zinc-700">{{ selectedUser.joined | date:'MMM d, y' }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-zinc-500">Last active</span>
          <span class="text-zinc-700">{{ selectedUser.updatedAt | date:'MMM d, y' }}</span>
        </div>
        <div *ngIf="selectedUser.deletedAt" class="flex items-center justify-between">
          <span class="text-zinc-500">Deleted</span>
          <span class="text-red-600">{{ selectedUser.deletedAt | date:'MMM d, y' }}</span>
        </div>
        <div class="border-t border-zinc-100 pt-2">
          <span class="font-mono text-[10px] text-zinc-300">{{ selectedUser.id }}</span>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- ── Create admin user panel ─────────────────────────────────────────── -->
<div
  class="fixed inset-0 z-50"
  [class.pointer-events-none]="!showCreatePanel"
  (click)="closeCreatePanel()"
>
  <div
    class="absolute inset-0 bg-black/30 backdrop-blur-[1px] transition-opacity duration-200"
    [class.opacity-0]="!showCreatePanel"
    [class.opacity-100]="showCreatePanel"
  ></div>
  <div
    class="absolute inset-y-0 right-0 flex w-[400px] flex-col bg-white shadow-2xl transition-transform duration-200"
    [class.translate-x-full]="!showCreatePanel"
    [class.translate-x-0]="showCreatePanel"
    (click)="$event.stopPropagation()"
  >
    <div class="flex flex-shrink-0 items-center justify-between border-b border-zinc-200 px-5 py-4">
      <div>
        <p class="text-sm font-semibold text-zinc-900">Create admin user</p>
        <p class="mt-0.5 text-xs text-zinc-400">Creates a Cognito account and adds it to the Admins group</p>
      </div>
      <button (click)="closeCreatePanel()" class="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600">
        <lucide-icon name="circle-x" [size]="16" [strokeWidth]="1.75"></lucide-icon>
      </button>
    </div>
    <div class="flex-1 overflow-y-auto px-5 py-4">
      <div *ngIf="createError" class="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
        <lucide-icon name="circle-alert" [size]="13" class="mt-0.5 flex-shrink-0" [strokeWidth]="2"></lucide-icon>
        {{ createError }}
      </div>
      <div class="space-y-4">
        <div>
          <label class="mb-1 block text-xs font-medium text-zinc-700">Email</label>
          <input type="email" [(ngModel)]="createForm.email" placeholder="admin@example.com"
            class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="mb-1 block text-xs font-medium text-zinc-700">First name</label>
            <input type="text" [(ngModel)]="createForm.givenName" placeholder="Jane"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-zinc-700">Last name</label>
            <input type="text" [(ngModel)]="createForm.familyName" placeholder="Smith"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
          </div>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-zinc-700">Initial password</label>
          <input type="password" [(ngModel)]="createForm.temporaryPassword" placeholder="Min 12 chars, upper + lower + digit + symbol"
            class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
          <p class="mt-1 text-[11px] text-zinc-400">Share this with the new admin. They can change it after sign-in.</p>
        </div>
      </div>
    </div>
    <div class="flex flex-shrink-0 items-center justify-end gap-2 border-t border-zinc-200 px-5 py-4">
      <button (click)="closeCreatePanel()" class="rounded-lg border border-zinc-200 px-4 py-2 text-xs font-medium text-zinc-600 hover:bg-zinc-50">Cancel</button>
      <button
        (click)="submitCreate()"
        [disabled]="createLoading || !createForm.email || !createForm.givenName || !createForm.familyName || !createForm.temporaryPassword"
        class="flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <lucide-icon *ngIf="createLoading" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
        {{ createLoading ? 'Creating...' : 'Create admin' }}
      </button>
    </div>
  </div>
</div>

